import { useState, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CreditCard, Loader2, Tag, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { PlanDef, AddOnDef, ANNUAL_DISCOUNT } from "./plans-data";
import { PRICE_PER_CHILD, MIN_CHARGE } from "./PerChildCard";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  selectedPlan: string | null;
  selectedAddOns: string[];
  billingCycle: "monthly" | "yearly";
  getPrice: (monthly: number) => number;
  plans: PlanDef[];
  addOns: AddOnDef[];
  currentSub: any;
  isTrial: boolean;
  nurseryId?: string;
  perChildMode?: boolean;
  childrenCount?: number;
}

const SubscriptionConfirmDialog = ({
  open, onOpenChange, selectedPlan, selectedAddOns, billingCycle,
  getPrice, plans, addOns, currentSub, isTrial, nurseryId,
  perChildMode = false, childrenCount = 0
}: Props) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [paymentMethod, setPaymentMethod] = useState("bank_transfer");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState<{ code: string; discount_percent: number } | null>(null);

  const planData = perChildMode ? null : plans.find((p) => p.id === selectedPlan);
  if (!perChildMode && !planData) return null;

  // Per-child calculations
  const perChildTotal = perChildMode ? Math.max(childrenCount * PRICE_PER_CHILD, MIN_CHARGE) : 0;

  const planPrice = perChildMode ? perChildTotal : getPrice(planData!.monthlyPrice);
  const addOnsTotal = perChildMode ? 0 : selectedAddOns.reduce((sum, id) => {
    const addon = addOns.find((a) => a.id === id);
    return sum + (addon ? getPrice(addon.monthlyPrice) : 0);
  }, 0);

  const subtotal = planPrice + addOnsTotal;
  const originalTotal = perChildMode ? perChildTotal : (billingCycle === "yearly"
    ? (planData!.monthlyPrice + selectedAddOns.reduce((s, id) => s + (addOns.find((a) => a.id === id)?.monthlyPrice || 0), 0)) * 12
    : subtotal);

  const promoDiscount = appliedPromo ? Math.round(subtotal * appliedPromo.discount_percent / 100) : 0;
  const totalAmount = subtotal - promoDiscount;

  const applyPromoCode = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);

    const { data, error } = await supabase.rpc("validate_promo_code", {
      _code: promoCode.trim(),
      _plan: selectedPlan || null,
    });

    setPromoLoading(false);

    if (error) {
      toast.error("خطأ في التحقق من الكود");
      return;
    }

    const result = data as any;
    if (!result?.valid) {
      toast.error(result?.error || "الكود غير صالح");
      return;
    }

    setAppliedPromo({ code: result.code, discount_percent: result.discount_percent });
    toast.success(`تم تطبيق الكود! خصم ${result.discount_percent}% 🎉`);
  };

  const removePromo = () => {
    setAppliedPromo(null);
    setPromoCode("");
  };

  const handleConfirm = async () => {
    if (!selectedPlan || !nurseryId || !user) return;
    setLoading(true);

    const totalDiscount = perChildMode ? (appliedPromo?.discount_percent || 0) : ((billingCycle === "yearly" ? ANNUAL_DISCOUNT : 0) + (appliedPromo?.discount_percent || 0));
    const requestType = !currentSub || isTrial ? "new" : (selectedPlan !== currentSub.plan ? "upgrade" : "renewal");

    const { data: inserted, error } = await supabase
      .from("subscription_requests")
      .insert({
        nursery_id: nurseryId,
        requested_by: user.id,
        request_type: requestType,
        current_plan: currentSub?.plan || null,
        requested_plan: selectedPlan,
        billing_cycle: billingCycle,
        amount: totalAmount,
        original_amount: originalTotal,
        discount_percent: totalDiscount,
        discount_type: appliedPromo ? "promo" : (billingCycle === "yearly" ? "annual" : "none"),
        payment_method: paymentMethod,
        promo_code: appliedPromo?.code || null,
        add_ons: selectedAddOns.length ? selectedAddOns : null,
        notes: notes || null,
        status: "pending",
      } as any)
      .select()
      .single();

    if (error || !inserted) {
      console.error("Subscription request error:", error);
      toast.error("خطأ في إرسال الطلب: " + (error?.message || "لم يتم حفظ الطلب - تحقق من الصلاحيات"));
    } else {
      toast.success("تم إرسال طلب الاشتراك بنجاح! سيتم مراجعته من المشرف العام ✅");
      queryClient.invalidateQueries({ queryKey: ["my-subscription"] });
      queryClient.invalidateQueries({ queryKey: ["my-subscription-requests"] });
    }

    setLoading(false);
    onOpenChange(false);
    setNotes("");
    setAppliedPromo(null);
    setPromoCode("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" dir="rtl">
        <DialogHeader>
          <DialogTitle>تأكيد {isTrial || !currentSub ? "الاشتراك" : "التجديد/الترقية"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg border p-4 bg-muted/30 space-y-2">
            <div className="flex justify-between text-sm">
              <span>الخطة</span>
              <span className="font-medium">{perChildMode ? "اشتراك بالطفل 👶" : planData!.name}</span>
            </div>
            {perChildMode ? (
              <>
                <div className="flex justify-between text-sm">
                  <span>عدد الأطفال</span>
                  <span className="font-bold">{childrenCount} طفل</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>سعر الطفل</span>
                  <span>{PRICE_PER_CHILD} ج.م</span>
                </div>
                {childrenCount < 50 && (
                  <div className="flex justify-between text-sm text-amber-600">
                    <span>⚠️ الحد الأدنى (50 طفل)</span>
                    <span>{MIN_CHARGE} ج.م</span>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="flex justify-between text-sm">
                  <span>الدورة</span>
                  <span>{billingCycle === "yearly" ? "سنوي" : "شهري"}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>سعر الخطة</span>
                  <span>{planPrice.toLocaleString()} ج.م</span>
                </div>
              </>
            )}
            {selectedAddOns.map((id) => {
              const addon = addOns.find((a) => a.id === id);
              if (!addon) return null;
              return (
                <div key={id} className="flex justify-between text-sm text-muted-foreground">
                  <span>+ {addon.name}</span>
                  <span>{getPrice(addon.monthlyPrice).toLocaleString()} ج.م</span>
                </div>
              );
            })}
            {billingCycle === "yearly" && (
              <div className="flex justify-between text-sm text-green-600">
                <span>خصم سنوي ({ANNUAL_DISCOUNT}%)</span>
                <span>- {(originalTotal - subtotal).toLocaleString()} ج.م</span>
              </div>
            )}
            {appliedPromo && (
              <div className="flex justify-between text-sm text-green-600">
                <span>كود ترويجي ({appliedPromo.code} - {appliedPromo.discount_percent}%)</span>
                <span>- {promoDiscount.toLocaleString()} ج.م</span>
              </div>
            )}
            <div className="flex justify-between font-bold border-t pt-2">
              <span>الإجمالي</span>
              <span>{totalAmount.toLocaleString()} ج.م</span>
            </div>
          </div>

          {/* Promo Code */}
          <div>
            <Label className="flex items-center gap-1">
              <Tag className="w-3.5 h-3.5" />
              كود ترويجي (اختياري)
            </Label>
            {appliedPromo ? (
              <div className="flex items-center gap-2 mt-1 p-2 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900/30">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="font-mono font-bold text-green-700 dark:text-green-400">{appliedPromo.code}</span>
                <span className="text-sm text-green-600">(-{appliedPromo.discount_percent}%)</span>
                <Button variant="ghost" size="sm" className="mr-auto text-xs" onClick={removePromo}>إزالة</Button>
              </div>
            ) : (
              <div className="flex gap-2 mt-1">
                <Input
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  placeholder="NUR-XXXX"
                  dir="ltr"
                  className="font-mono"
                />
                <Button variant="outline" onClick={applyPromoCode} disabled={promoLoading || !promoCode.trim()}>
                  {promoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "تطبيق"}
                </Button>
              </div>
            )}
          </div>

          <div>
            <Label>طريقة الدفع</Label>
            <Select value={paymentMethod} onValueChange={setPaymentMethod}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="bank_transfer">تحويل بنكي</SelectItem>
                <SelectItem value="cash">نقدي</SelectItem>
                <SelectItem value="online">دفع إلكتروني</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>ملاحظات (اختياري)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="مثال: رقم الحوالة البنكية..." />
          </div>

          <Button className="w-full" onClick={handleConfirm} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              <>
                <CreditCard className="w-4 h-4 ml-2" />
                إرسال طلب الاشتراك للمراجعة
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionConfirmDialog;
